import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { 
  Download, FileType, FileSpreadsheet, Sheet, FileText,
  Printer, Share2, Image, Lock,
} from 'lucide-react';
import ShareModal from '../Modals/ShareModal';
import {
  downloadCSV, downloadText, downloadExcel,
  downloadPDF, downloadPNG, triggerPrint,
} from '../../utils/exportFormats';
import { trackExport, trackPrint, checkPrintQuota } from '../../services/api';
import { buildExportMeta } from './buildExportMeta';

// Fire a window event so live output counters in the Profile modal can
// update without threading new props through DataView → Header → Profile.
const notifyOutputTracked = (kind, result) => {
  if (!result || result.allowed === false) return;
  try {
    window.dispatchEvent(new CustomEvent('krime:output-tracked', {
      detail: { kind, ...result }
    }));
  } catch { /* noop */ }
};

const EXPORT_FORMATS = ['text', 'pdf', 'csv', 'excel', 'png'];
const PLANS_WITH_EXPORTS = ['captain', 'chief', 'enterprise'];

const TableExportActions = ({ locationName, exportContext, userPlan, onRequestUpgrade }) => {
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [showShareModal, setShowShareModal]     = useState(false);
  const [busy, setBusy] = useState(null);
  const [downloadMenuStyle, setDownloadMenuStyle] = useState(null);
  const downloadAnchorRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.download-menu') && !event.target.closest('.download-button-table')) {
        setShowDownloadMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => document.removeEventListener('mousedown', handleClickOutside, true);
  }, []);

  const computeDownloadMenuPosition = () => {
    const el = downloadAnchorRef.current;
    if (!el) return null;
    const r = el.getBoundingClientRect();
    return {
      position: 'fixed',
      top: r.bottom + 4,
      right: Math.max(8, window.innerWidth - r.right),
      left: 'auto',
      zIndex: 10050,
    };
  };

  useLayoutEffect(() => {
    if (!showDownloadMenu) return;
    const updatePosition = () => {
      const next = computeDownloadMenuPosition();
      if (next) setDownloadMenuStyle(next);
    };
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [showDownloadMenu]);

  useEffect(() => {
    if (!showDownloadMenu) setDownloadMenuStyle(null);
  }, [showDownloadMenu]);

  const toggleDownloadMenu = () => {
    if (showDownloadMenu) {
      setShowDownloadMenu(false);
      return;
    }
    const pos = computeDownloadMenuPosition();
    if (!pos) return;
    setDownloadMenuStyle(pos);
    setShowDownloadMenu(true);
  };

  const plan = userPlan || 'free';
  const canExport = PLANS_WITH_EXPORTS.includes(plan);

  const handleExport = async (format) => {
    if (EXPORT_FORMATS.includes(format) && !canExport) {
      onRequestUpgrade?.('export_limit');
      setShowDownloadMenu(false);
      return;
    }

    setShowDownloadMenu(false);
    setBusy(format);
    try {
      if (!exportContext) return;

      if (EXPORT_FORMATS.includes(format)) {
        const result = await trackExport(
          buildExportMeta(exportContext, { format })
        ).catch(() => null);
        if (result && result.allowed === false) {
          onRequestUpgrade?.('export_limit');
          return;
        }
        notifyOutputTracked('export', result);

        if (format === 'csv')   await downloadCSV(exportContext);
        if (format === 'text')  await downloadText(exportContext);
        if (format === 'excel') await downloadExcel(exportContext);
        if (format === 'pdf')   await downloadPDF(exportContext);
        if (format === 'png')   await downloadPNG(exportContext);
      }

      if (format === 'print') {
        const preflight = await checkPrintQuota().catch(() => null);
        if (preflight && preflight.allowed === false) {
          onRequestUpgrade?.('print_limit');
          return;
        }
        // Track immediately — triggerPrint opens an iframe print dialog;
        // afterprint fires on the iframe window, not the main window,
        // so the useEffect listener never catches it.
        trackPrint(buildExportMeta(exportContext))
          .then((result) => notifyOutputTracked('print', result))
          .catch(() => {});
        await triggerPrint(exportContext);
      }
    } catch (err) {
      console.error('Export error:', err);
    } finally {
      setBusy(null);
    }
  };

  const lockIcon = <Lock size={13} className="export-lock-icon export-lock-icon-free" />;

  const ExportRow = ({ format, label, Icon }) => (
    <button
      type="button"
      className={`dropdown-item export-dropdown-item${!canExport ? ' export-item-free-locked' : ''}`}
      onClick={() => handleExport(format)}
    >
      {canExport ? (
        <>
          <span className="export-format-icon"><Icon size={16} /></span>
          <span className="export-item-label">{label}</span>
        </>
      ) : (
        <>
          <span className="export-format-icon export-format-icon-muted"><Icon size={16} /></span>
          <span className="export-item-label">{label}</span>
          {lockIcon}
        </>
      )}
    </button>
  );

  return (
    <div className="export-actions">
      <div className="dropdown-container">
        <button 
          className="dropdown-button share-button-table"
          onClick={() => setShowShareModal(true)}
        >
          <Share2 size={20} />
        </button>
      </div>
      
      <div className="dropdown-container" ref={downloadAnchorRef}>
        <button 
          className={`dropdown-button download-button-table ${showDownloadMenu ? 'active' : ''} ${busy ? 'busy' : ''}`}
          onClick={toggleDownloadMenu}
          disabled={!!busy}
          title={busy ? `Exporting ${busy}…` : 'Download'}
        >
          <Download size={20} />
        </button>
        {showDownloadMenu && downloadMenuStyle && createPortal(
          <div className="dropdown-menu download-menu export-table-download-portal" style={downloadMenuStyle}>
            <ExportRow format="text" label="Text" Icon={FileType} />
            <ExportRow format="csv" label="CSV" Icon={FileSpreadsheet} />
            <ExportRow format="excel" label="Excel" Icon={Sheet} />
            <ExportRow format="pdf" label="PDF" Icon={FileText} />
            <ExportRow format="png" label="PNG" Icon={Image} />
            <button type="button" className="dropdown-item export-dropdown-item" onClick={() => handleExport('print')}>
              <span className="export-format-icon"><Printer size={16} /></span>
              <span className="export-item-label">Print</span>
            </button>
          </div>,
          document.body
        )}
      </div>

      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        shareUrl={window.location.href}
        locationName={locationName}
      />
    </div>
  );
};

export default TableExportActions;
